import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Plus, Trash2, Edit2, Power, RefreshCw, Search, AlertTriangle,
  Wifi, WifiOff, Battery, Settings, Download, Upload, Eye,
  Thermometer, Lightbulb, Lock, Tv, Wind, Shield, Router,
  ChevronDown, ChevronUp, X, Check, Filter, LayoutGrid, List,
  Zap, Clock, Activity, ArrowLeft, Layers, Building,
  Info, History, Radio, Droplets, ToggleLeft, ToggleRight,
  Cpu, MapPin, Calendar, User, TrendingUp
} from 'lucide-react';
import {
  getIoTHotelInfo,
  getIoTRooms,
  getIoTDevices,
  getIoTStatistics,
  getIoTFloors,
  addIoTDevice,
  updateIoTDevice,
  deleteIoTDevice,
  syncIoTDevice,
  executeIoTCommand,
  getIoTMaintenanceAlerts,
  discoverSmartThingsDevices,
  importSmartThingsDevices,
  syncAllSmartThingsDevices,
  getSmartThingsVirtualDevices,
  addVirtualDeviceToRoom,
  getVirtualDevicePrototypes,
  createVirtualDevice,
  getIoTAssignments,
  getIoTAssignmentHistory,
  createIoTBackup,
  getIoTBackups,
  getIoTDeviceDetail
} from '../services/managerAPI';
import { useAuth } from '../contexts/AuthContext';
import './ManagerIoTDashboard.css';

// Device category icons and colors
const CATEGORY_CONFIG = {
  climate: { icon: Thermometer, color: '#3B82F6', label: 'Climate Control' },
  lighting: { icon: Lightbulb, color: '#F59E0B', label: 'Lighting' },
  security: { icon: Lock, color: '#EF4444', label: 'Security' },
  entertainment: { icon: Tv, color: '#8B5CF6', label: 'Entertainment' },
  comfort: { icon: Wind, color: '#10B981', label: 'Comfort' },
  safety: { icon: Shield, color: '#F97316', label: 'Safety' },
  connectivity: { icon: Router, color: '#6366F1', label: 'Connectivity' }
};

const DEVICE_CATEGORIES = [
  { value: 'climate', label: 'Climate Control' },
  { value: 'lighting', label: 'Lighting' },
  { value: 'security', label: 'Security' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'comfort', label: 'Comfort' },
  { value: 'safety', label: 'Safety' },
  { value: 'connectivity', label: 'Connectivity' }
];

const DEVICE_STATUSES = [
  { value: 'online', label: 'Online', color: '#10B981' },
  { value: 'offline', label: 'Offline', color: '#6B7280' },
  { value: 'error', label: 'Error', color: '#EF4444' },
  { value: 'maintenance', label: 'Maintenance', color: '#F59E0B' }
];

const ManagerIoTDashboard = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  
  // Data state
  const [hotelInfo, setHotelInfo] = useState(null);
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [devices, setDevices] = useState([]);
  const [groupedDevices, setGroupedDevices] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [smartThingsDevices, setSmartThingsDevices] = useState([]);
  const [virtualDevices, setVirtualDevices] = useState([]);
  const [loadingVirtual, setLoadingVirtual] = useState(false);
  const [selectedVirtualDevice, setSelectedVirtualDevice] = useState(null); // device being assigned
  const [virtualAssignRoomId, setVirtualAssignRoomId] = useState('');
  const [assigningVirtualDevice, setAssigningVirtualDevice] = useState(false);
  // Create-new virtual device form state
  const [showCreateVirtualForm, setShowCreateVirtualForm] = useState(false);
  const [virtualPrototypes, setVirtualPrototypes] = useState([]);
  const [createVirtualName, setCreateVirtualName] = useState('');
  const [createVirtualPrototype, setCreateVirtualPrototype] = useState('');
  const [createVirtualRoomId, setCreateVirtualRoomId] = useState('');
  const [creatingVirtualDevice, setCreatingVirtualDevice] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [backups, setBackups] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [viewMode, setViewMode] = useState('floor'); // 'floor', 'grid' or 'list'
  const [selectedFloor, setSelectedFloor] = useState('all');
  const [selectedRoom, setSelectedRoom] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedDevice, setExpandedDevice] = useState(null);
  const [expandedFloors, setExpandedFloors] = useState({});
  
  // Device detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailDevice, setDetailDevice] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailTab, setDetailTab] = useState('info'); // 'info' | 'live' | 'history'

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDiscoverModal, setShowDiscoverModal] = useState(false);
  const [showVirtualModal, setShowVirtualModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [deletingDevice, setDeletingDevice] = useState(null);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  
  // Form state
  const [deviceForm, setDeviceForm] = useState({
    device_name: '',
    device_type: '',
    category: 'lighting',
    room_id: '',
    manufacturer: '',
    model: '',
    device_identifier: '',
    location_description: ''
  });
  
  // Load data on mount
  useEffect(() => {
    console.log('🔄 ManagerIoTDashboard useEffect triggered');
    console.log('   currentUser:', currentUser ? currentUser.email : 'null');
    console.log('   userProfile:', userProfile ? userProfile.email : 'null');
    
    if (currentUser && userProfile) {
      console.log('✅ Both currentUser and userProfile available, loading data...');
      loadAllData();
    } else {
      console.log('⏳ Waiting for auth - currentUser:', !!currentUser, 'userProfile:', !!userProfile);
    }
  }, [currentUser, userProfile]);
  
  const loadAllData = async () => {
    console.log('📡 loadAllData() called - starting API calls...');
    setLoading(true);
    try {
      console.log('📡 Calling getIoTHotelInfo...');
      const hotelPromise = loadHotelInfo();
      console.log('📡 Calling getIoTFloors...');
      const floorsPromise = loadFloors();
      console.log('📡 Calling getIoTRooms...');
      const roomsPromise = loadRooms();
      console.log('📡 Calling getIoTDevices...');
      const devicesPromise = loadDevices();
      console.log('📡 Calling getIoTStatistics...');
      const statsPromise = loadStatistics();
      console.log('📡 Calling getIoTMaintenanceAlerts...');
      const alertsPromise = loadAlerts();
      console.log('📡 Calling backup/history APIs...');
      const backupHistoryPromise = loadBackupAndHistory();
      
      await Promise.all([hotelPromise, floorsPromise, roomsPromise, devicesPromise, statsPromise, alertsPromise, backupHistoryPromise]);
      console.log('✅ All API calls completed');
    } catch (error) {
      console.error('❌ Error loading data:', error);
      toast.error('Failed to load IoT data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadHotelInfo = async () => {
    try {
      const response = await getIoTHotelInfo();
      if (response.success) {
        setHotelInfo(response.data);
      }
    } catch (error) {
      console.error('Error loading hotel info:', error);
    }
  };

  const loadFloors = async () => {
    try {
      console.log('🏢 Loading floors...');
      const response = await getIoTFloors();
      console.log('🏢 Floors response:', response);
      if (response.success) {
        console.log('🏢 Setting floors:', response.data);
        setFloors(response.data || []);
        // Initialize all floors as expanded
        const expanded = {};
        (response.data || []).forEach(f => {
          expanded[f.floor] = true;
        });
        setExpandedFloors(expanded);
        console.log('🏢 Floors state updated, count:', (response.data || []).length);
      }
    } catch (error) {
      console.error('❌ Error loading floors:', error);
    }
  };
  
  const loadRooms = async () => {
    try {
      // Load flat room list so every component can use rooms.map() directly
      const options = {};
      if (selectedFloor !== 'all') options.floor = selectedFloor;

      const response = await getIoTRooms(options);
      if (response.success) {
        const flat = Array.isArray(response.data)
          ? (response.data[0]?.rooms
              ? response.data.flatMap(f => f.rooms || [])  // unwrap grouped if needed
              : response.data)
          : [];
        setRooms(flat);
        // Auto-expand all floors so rooms show immediately
        const floorKeys = {};
        flat.forEach(r => { floorKeys[r.floor ?? 0] = true; });
        setExpandedFloors(floorKeys);
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
    }
  };
  
  const loadDevices = async () => {
    try {
      const filters = { groupByFloor: viewMode === 'floor' };
      if (selectedFloor !== 'all') filters.floor = selectedFloor;
      if (selectedRoom !== 'all') filters.roomId = selectedRoom;
      if (selectedCategory !== 'all') filters.category = selectedCategory;
      if (selectedStatus !== 'all') filters.status = selectedStatus;
      
      const response = await getIoTDevices(filters);
      if (response.success) {
        if (response.grouped) {
          setGroupedDevices(response.data || []);
          // Flatten for filtering purposes
          const flat = (response.data || []).flatMap(f => f.devices || []);
          setDevices(flat);
        } else {
          setDevices(response.data || []);
          setGroupedDevices([]);
        }
      }
    } catch (error) {
      console.error('Error loading devices:', error);
    }
  };
  
  const loadStatistics = async () => {
    try {
      const response = await getIoTStatistics();
      if (response.success) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };
  
  const loadAlerts = async () => {
    try {
      const response = await getIoTMaintenanceAlerts();
      if (response.success) {
        setAlerts(response.data || []);
      }
    } catch (error) {
      console.error('Error loading alerts:', error);
    }
  };

  const loadBackupAndHistory = async () => {
    try {
      const [assignmentsResponse, historyResponse, backupsResponse] = await Promise.all([
        getIoTAssignments(),
        getIoTAssignmentHistory(15),
        getIoTBackups(10)
      ]);

      if (assignmentsResponse.success) {
        setAssignments(assignmentsResponse.data || []);
      }
      if (historyResponse.success) {
        setAssignmentHistory(historyResponse.data || []);
      }
      if (backupsResponse.success) {
        setBackups(backupsResponse.data || []);
      }
    } catch (error) {
      console.error('Error loading backup/history data:', error);
    }
  };

  // Reload devices when filters change
  useEffect(() => {
    if (!loading && currentUser && userProfile) {
      loadDevices();
    }
  }, [selectedFloor, selectedRoom, selectedCategory, selectedStatus, viewMode]);

  // Toggle floor expansion
  const toggleFloorExpansion = (floor) => {
    setExpandedFloors(prev => ({
      ...prev,
      [floor]: !prev[floor]
    }));
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleSyncAllDevices = async () => {
    setSyncingAll(true);
    try {
      const response = await syncAllSmartThingsDevices();
      if (response.success) {
        const { syncedCount = 0, importedCount = 0, errorCount = 0 } = response.data || {};
        toast.success(`Sync complete: ${syncedCount} updated, ${importedCount} imported${errorCount ? `, ${errorCount} errors` : ''}`);
        await loadAllData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to sync SmartThings devices');
    } finally {
      setSyncingAll(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      const backupName = `iot-backup-${new Date().toISOString().split('T')[0]}`;
      const response = await createIoTBackup({ backupName, backupType: 'full' });
      if (response.success) {
        toast.success('IoT backup created successfully');
        await loadBackupAndHistory();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create backup');
    } finally {
      setCreatingBackup(false);
    }
  };
  
  // Filter devices based on search (for non-floor views)
  const filteredDevices = devices.filter(device => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        device.device_name?.toLowerCase().includes(search) ||
        device.device_type?.toLowerCase().includes(search) ||
        device.room_number?.toLowerCase().includes(search) ||
        device.manufacturer?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  // Filter grouped devices based on search (for floor view)
  const getFilteredGroupedDevices = () => {
    if (!searchTerm) return groupedDevices;
    
    return groupedDevices.map(floorGroup => ({
      ...floorGroup,
      devices: floorGroup.devices.filter(device => {
        const search = searchTerm.toLowerCase();
        return (
          device.device_name?.toLowerCase().includes(search) ||
          device.device_type?.toLowerCase().includes(search) ||
          device.room_number?.toLowerCase().includes(search) ||
          device.manufacturer?.toLowerCase().includes(search)
        );
      })
    })).filter(floorGroup => floorGroup.devices.length > 0);
  };
  
  // Group devices by room for list view
  const devicesByRoom = filteredDevices.reduce((acc, device) => {
    const roomKey = device.room_number || 'Unassigned';
    if (!acc[roomKey]) acc[roomKey] = [];
    acc[roomKey].push(device);
    return acc;
  }, {});
  
  // Device CRUD handlers
  const handleAddDevice = async (e) => {
    e.preventDefault();
    try {
      const response = await addIoTDevice({
        ...deviceForm,
        room_id: deviceForm.room_id || null,
        sync_to_smartthings: !!deviceForm.device_identifier
      });
      
      if (response.success) {
        toast.success('Device added successfully');
        setShowAddModal(false);
        resetForm();
        loadDevices();
        loadStatistics();
      }
    } catch (error) {
      toast.error('Failed to add device: ' + (error.response?.data?.message || error.message));
    }
  };
  
  const handleEditDevice = async (e) => {
    e.preventDefault();
    try {
      const response = await updateIoTDevice(editingDevice.device_id, deviceForm);
      
      if (response.success) {
        toast.success('Device updated successfully');
        setShowEditModal(false);
        setEditingDevice(null);
        resetForm();
        loadDevices();
      }
    } catch (error) {
      toast.error('Failed to update device: ' + (error.response?.data?.message || error.message));
    }
  };
  
  const handleDeleteDevice = async (hardDelete = false) => {
    try {
      const response = await deleteIoTDevice(
        deletingDevice.device_id,
        hardDelete,
        !!deletingDevice.device_identifier
      );
      
      if (response.success) {
        toast.success(hardDelete ? 'Device permanently deleted' : 'Device removed');
        setShowDeleteModal(false);
        setDeletingDevice(null);
        loadDevices();
        loadStatistics();
      }
    } catch (error) {
      toast.error('Failed to delete device: ' + (error.response?.data?.message || error.message));
    }
  };
  
  const handleSyncDevice = async (device) => {
    if (!device.device_identifier) {
      toast.warning('Device not linked to SmartThings');
      return;
    }
    
    try {
      await syncIoTDevice(device.device_id);
      toast.success('Device synced successfully');
      loadDevices();
    } catch (error) {
      toast.error('Failed to sync device');
    }
  };
  
  const handleToggleDevice = async (device) => {
    if (!device.device_identifier) {
      toast.warning('Device not linked to SmartThings');
      return;
    }
    
    try {
      const command = device.status === 'online' ? 'off' : 'on';
      await executeIoTCommand(device.device_id, 'switch', command);
      toast.success(`Device turned ${command}`);
      setTimeout(loadDevices, 1000);
    } catch (error) {
      toast.error('Failed to toggle device');
    }
  };
  
  // SmartThings virtual devices
  const handleViewVirtualDevices = async () => {
    setShowVirtualModal(true);
    setSelectedVirtualDevice(null);
    setVirtualAssignRoomId('');
    setLoadingVirtual(true);
    try {
      const response = await getSmartThingsVirtualDevices();
      if (response.success) {
        setVirtualDevices(response.data || []);
      } else {
        toast.error(response.message || 'Failed to load virtual devices');
        setVirtualDevices([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load virtual devices');
      setVirtualDevices([]);
    } finally {
      setLoadingVirtual(false);
    }
  };

  const handleAssignVirtualDevice = async () => {
    if (!selectedVirtualDevice || !virtualAssignRoomId) return;
    setAssigningVirtualDevice(true);
    try {
      const response = await addVirtualDeviceToRoom(
        selectedVirtualDevice.deviceId,
        parseInt(virtualAssignRoomId, 10),
        selectedVirtualDevice.label
      );
      if (response.success) {
        toast.success(response.message || 'Device assigned to room');
        if (response.data?.smartthingsSynced) {
          toast.info('SmartThings room assignment synced', { autoClose: 2000 });
        }
        setSelectedVirtualDevice(null);
        setVirtualAssignRoomId('');
        // refresh virtual list and main device list
        handleViewVirtualDevices();
        loadAllData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to assign device to room');
    } finally {
      setAssigningVirtualDevice(false);
    }
  };

  const handleOpenCreateVirtualForm = async () => {
    setShowCreateVirtualForm(true);
    setSelectedVirtualDevice(null);
    setCreateVirtualName('');
    setCreateVirtualPrototype('');
    setCreateVirtualRoomId('');
    if (virtualPrototypes.length === 0) {
      try {
        const res = await getVirtualDevicePrototypes();
        setVirtualPrototypes(res.data || []);
      } catch {
        toast.error('Failed to load device prototypes');
      }
    }
  };

  const handleCreateVirtualDevice = async () => {
    if (!createVirtualName.trim() || !createVirtualPrototype) {
      toast.warn('Device name and type are required');
      return;
    }
    setCreatingVirtualDevice(true);
    try {
      const response = await createVirtualDevice(
        createVirtualName.trim(),
        createVirtualPrototype,
        createVirtualRoomId ? parseInt(createVirtualRoomId, 10) : null
      );
      if (response.success) {
        toast.success(response.message || 'Virtual device created!');
        if (response.data?.smartthingsRoomSynced) {
          toast.info('Assigned to SmartThings room', { autoClose: 2000 });
        }
        setShowCreateVirtualForm(false);
        setCreateVirtualName('');
        setCreateVirtualPrototype('');
        setCreateVirtualRoomId('');
        // Refresh list
        handleViewVirtualDevices();
        if (response.data?.roomId) loadAllData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create virtual device');
    } finally {
      setCreatingVirtualDevice(false);
    }
  };

  // SmartThings discovery
  const handleDiscoverDevices = async () => {
    setShowDiscoverModal(true);
    setSelectedDeviceIds([]); // Reset selection when opening modal
    try {
      const response = await discoverSmartThingsDevices();
      if (response.success) {
        setSmartThingsDevices(response.data.devices || []);
      }
    } catch (error) {
      toast.error('Failed to discover SmartThings devices');
      setSmartThingsDevices([]);
    }
  };
  
  const handleImportDevices = async (deviceIds) => {
    if (!deviceIds || deviceIds.length === 0) {
      toast.error('No devices selected');
      return;
    }

    console.log('Importing devices:', deviceIds);
    
    try {
      const response = await importSmartThingsDevices(deviceIds);
      console.log('Import response:', response);
      
      if (response.success) {
        toast.success(`Imported ${response.data.total_imported} devices`);
        setShowDiscoverModal(false);
        setSelectedDeviceIds([]); // Reset selection after import
        loadDevices();
        loadStatistics();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.message || 'Failed to import devices');
    }
  };
  
  // Form handlers
  const resetForm = () => {
    setDeviceForm({
      device_name: '',
      device_type: '',
      category: 'lighting',
      room_id: '',
      manufacturer: '',
      model: '',
      device_identifier: '',
      location_description: ''
    });
  };
  
  const openEditModal = (device) => {
    setEditingDevice(device);
    setDeviceForm({
      device_name: device.device_name || '',
      device_type: device.device_type || '',
      category: device.category || 'lighting',
      room_id: device.room_id || '',
      manufacturer: device.manufacturer || '',
      model: device.model || '',
      device_identifier: device.device_identifier || '',
      location_description: device.location_description || ''
    });
    setShowEditModal(true);
  };
  
  const openDeleteModal = (device) => {
    setDeletingDevice(device);
    setShowDeleteModal(true);
  };
  
  // Get category config for a device
  const getCategoryConfig = (category) => {
    return CATEGORY_CONFIG[category] || CATEGORY_CONFIG.connectivity;
  };
  
  // Render statistics cards
  const renderStatistics = () => {
    if (!statistics) return null;
    
    const { summary, byCategory } = statistics;
    
    return (
      <div className="iot-stats-grid">
        <div className="iot-stat-card total">
          <div className="stat-icon">
            <Activity size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{summary.total_devices || 0}</div>
            <div className="stat-label">Total Devices</div>
          </div>
        </div>
        
        <div className="iot-stat-card online">
          <div className="stat-icon">
            <Wifi size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{summary.online_devices || 0}</div>
            <div className="stat-label">Online</div>
          </div>
        </div>
        
        <div className="iot-stat-card offline">
          <div className="stat-icon">
            <WifiOff size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{summary.offline_devices || 0}</div>
            <div className="stat-label">Offline</div>
          </div>
        </div>
        
        <div className="iot-stat-card error">
          <div className="stat-icon">
            <AlertTriangle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{summary.error_devices || 0}</div>
            <div className="stat-label">Errors</div>
          </div>
        </div>
        
        <div className="iot-stat-card battery">
          <div className="stat-icon">
            <Battery size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{summary.low_battery_devices || 0}</div>
            <div className="stat-label">Low Battery</div>
          </div>
        </div>
        
        <div className="iot-stat-card rooms">
          <div className="stat-icon">
            <LayoutGrid size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{summary.rooms_with_devices || 0}</div>
            <div className="stat-label">Rooms w/ Devices</div>
          </div>
        </div>

        <div className="iot-stat-card floors">
          <div className="stat-icon">
            <Building size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{floors.length || 0}</div>
            <div className="stat-label">Floors</div>
          </div>
        </div>
      </div>
    );
  };
  
  // Render alerts section
  const renderAlerts = () => {
    if (!alerts || alerts.length === 0) return null;
    
    return (
      <div className="iot-alerts-section">
        <h3>
          <AlertTriangle size={20} />
          Maintenance Alerts ({alerts.length})
        </h3>
        <div className="alerts-list">
          {alerts.slice(0, 5).map((alert, index) => (
            <div key={index} className={`alert-item priority-${alert.priority}`}>
              <div className="alert-icon">
                {alert.priority === 'critical' && <AlertTriangle size={16} />}
                {alert.priority === 'high' && <WifiOff size={16} />}
                {alert.priority === 'medium' && <Battery size={16} />}
                {alert.priority === 'low' && <Settings size={16} />}
              </div>
              <div className="alert-content">
                <span className="alert-device">{alert.device_name}</span>
                <span className="alert-type">{alert.alert_type}</span>
                {alert.room_number && (
                  <span className="alert-room">Room {alert.room_number}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderBackupHistory = () => {
    return (
      <div className="iot-extra-section">
        <div className="iot-extra-grid">
          <div className="iot-extra-card">
            <div className="iot-extra-header">
              <h3>
                <Zap size={18} />
                Assignment Snapshot
              </h3>
            </div>
            <div className="iot-extra-stats">
              <div className="extra-stat">
                <span className="value">{assignments.length}</span>
                <span className="label">Active Assignments</span>
              </div>
              <div className="extra-stat">
                <span className="value">{assignmentHistory.length}</span>
                <span className="label">Recent Activity</span>
              </div>
              <div className="extra-stat">
                <span className="value">{backups.length}</span>
                <span className="label">Backups</span>
              </div>
            </div>
          </div>

          <div className="iot-extra-card">
            <div className="iot-extra-header">
              <h3>
                <Clock size={18} />
                Recent Assignment Activity
              </h3>
            </div>
            {assignmentHistory.length === 0 ? (
              <p className="empty-text">No assignment activity yet.</p>
            ) : (
              <div className="history-list">
                {assignmentHistory.slice(0, 6).map((item) => (
                  <div key={item.history_id} className={`history-item status-${item.status || 'success'}`}>
                    <div className="history-main">
                      <span className="action">{item.action}</span>
                      <span className="device">{item.device_name || 'Unknown device'}</span>
                    </div>
                    <div className="history-meta">
                      <span>{item.room_number ? `Room ${item.room_number}` : 'No room'}</span>
                      <span>{new Date(item.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="iot-extra-card">
            <div className="iot-extra-header">
              <h3>
                <Download size={18} />
                Backup Snapshots
              </h3>
            </div>
            {backups.length === 0 ? (
              <p className="empty-text">No backups created yet.</p>
            ) : (
              <div className="backup-list">
                {backups.slice(0, 6).map((backup) => (
                  <div key={backup.backup_id} className="backup-item">
                    <div className="backup-main">
                      <span className="name">{backup.backup_name}</span>
                      <span className="type">{backup.backup_type}</span>
                    </div>
                    <div className="backup-meta">
                      <span>{backup.device_count || 0} devices</span>
                      <span>{new Date(backup.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // Render device card
  const openDeviceDetail = async (device) => {
    setDetailDevice({ device, history: [], liveStatus: null }); // show modal immediately with card data
    setDetailTab('info');
    setShowDetailModal(true);
    setLoadingDetail(true);
    try {
      const resp = await getIoTDeviceDetail(device.device_id);
      if (resp.success) {
        setDetailDevice(resp.data);
      }
    } catch (err) {
      toast.error('Could not load full device details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const renderDeviceCard = (device) => {
    const config = getCategoryConfig(device.category);
    const Icon = config.icon;
    const isExpanded = expandedDevice === device.device_id;
    
    return (
      <div 
        key={device.device_id} 
        className={`iot-device-card ${device.status} ${isExpanded ? 'expanded' : ''}`}
      >
        <div className="device-card-header" onClick={() => openDeviceDetail(device)} style={{ cursor: 'pointer' }}>
          <div className="device-icon" style={{ backgroundColor: config.color + '20', color: config.color }}>
            <Icon size={24} />
          </div>
          <div className="device-info">
            <h4>{device.device_name}</h4>
            <span className="device-type">{device.device_type}</span>
            {device.room_number && (
              <span className="device-room">Room {device.room_number}</span>
            )}
          </div>
          <div className={`device-status-badge ${device.status}`}>
            {device.status === 'online' && <Wifi size={14} />}
            {device.status === 'offline' && <WifiOff size={14} />}
            {device.status === 'error' && <AlertTriangle size={14} />}
            {device.status}
          </div>
        </div>
        
        <div className="device-card-meta">
          <span className="meta-item">
            <Settings size={14} />
            {device.manufacturer || 'Unknown'}
          </span>
          {device.battery_level && (
            <span className={`meta-item ${device.battery_level < 20 ? 'low-battery' : ''}`}>
              <Battery size={14} />
              {device.battery_level}%
            </span>
          )}
          {device.device_identifier && (
            <span className="meta-item linked">
              <Zap size={14} />
              SmartThings
            </span>
          )}
        </div>
        
        <div className="device-card-actions">
          {device.device_identifier && device.category === 'lighting' && (
            <button 
              className="action-btn power"
              onClick={() => handleToggleDevice(device)}
              title="Toggle Power"
            >
              <Power size={16} />
            </button>
          )}
          <button 
            className="action-btn sync"
            onClick={() => handleSyncDevice(device)}
            title="Sync Status"
          >
            <RefreshCw size={16} />
          </button>
          <button 
            className="action-btn edit"
            onClick={() => openEditModal(device)}
            title="Edit Device"
          >
            <Edit2 size={16} />
          </button>
          <button 
            className="action-btn delete"
            onClick={() => openDeleteModal(device)}
            title="Delete Device"
          >
            <Trash2 size={16} />
          </button>
          <button 
            className="action-btn expand"
            onClick={() => setExpandedDevice(isExpanded ? null : device.device_id)}
            title="Show Details"
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
        
        {isExpanded && (
          <div className="device-card-details">
            <div className="detail-row">
              <span className="detail-label">Model:</span>
              <span className="detail-value">{device.model || 'Unknown'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Category:</span>
              <span className="detail-value">{config.label}</span>
            </div>
            {device.location_description && (
              <div className="detail-row">
                <span className="detail-label">Location:</span>
                <span className="detail-value">{device.location_description}</span>
              </div>
            )}
            {device.last_seen && (
              <div className="detail-row">
                <span className="detail-label">Last Seen:</span>
                <span className="detail-value">
                  {new Date(device.last_seen).toLocaleString()}
                </span>
              </div>
            )}
            {device.device_identifier && (
              <div className="detail-row">
                <span className="detail-label">SmartThings ID:</span>
                <span className="detail-value code">{device.device_identifier}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  
  // Render device form modal
  const renderDeviceFormModal = (isEdit = false) => {
    const title = isEdit ? 'Edit Device' : 'Add New Device';
    const onSubmit = isEdit ? handleEditDevice : handleAddDevice;
    
    return (
      <div className="modal-overlay" onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{title}</h2>
            <button className="modal-close" onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>
              <X size={20} />
            </button>
          </div>
          
          <form onSubmit={onSubmit} className="device-form">
            <div className="form-row">
              <div className="form-group">
                <label>Device Name *</label>
                <input
                  type="text"
                  value={deviceForm.device_name}
                  onChange={e => setDeviceForm({...deviceForm, device_name: e.target.value})}
                  placeholder="e.g., Smart Light"
                  required
                />
              </div>
              <div className="form-group">
                <label>Device Type *</label>
                <input
                  type="text"
                  value={deviceForm.device_type}
                  onChange={e => setDeviceForm({...deviceForm, device_type: e.target.value})}
                  placeholder="e.g., Light Switch"
                  required
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Category *</label>
                <select
                  value={deviceForm.category}
                  onChange={e => setDeviceForm({...deviceForm, category: e.target.value})}
                  required
                >
                  {DEVICE_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Room</label>
                <select
                  value={deviceForm.room_id}
                  onChange={e => setDeviceForm({...deviceForm, room_id: e.target.value})}
                >
                  <option value="">-- Unassigned --</option>
                  {rooms.map(room => (
                    <option key={room.room_id} value={room.room_id}>
                      Room {room.room_number} ({room.room_type})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Manufacturer</label>
                <input
                  type="text"
                  value={deviceForm.manufacturer}
                  onChange={e => setDeviceForm({...deviceForm, manufacturer: e.target.value})}
                  placeholder="e.g., Samsung"
                />
              </div>
              <div className="form-group">
                <label>Model</label>
                <input
                  type="text"
                  value={deviceForm.model}
                  onChange={e => setDeviceForm({...deviceForm, model: e.target.value})}
                  placeholder="e.g., ST-100"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>SmartThings Device ID</label>
              <input
                type="text"
                value={deviceForm.device_identifier}
                onChange={e => setDeviceForm({...deviceForm, device_identifier: e.target.value})}
                placeholder="Paste SmartThings device ID to link"
              />
              <small className="form-hint">
                Link to an existing SmartThings device for remote control
              </small>
            </div>
            
            <div className="form-group">
              <label>Location Description</label>
              <input
                type="text"
                value={deviceForm.location_description}
                onChange={e => setDeviceForm({...deviceForm, location_description: e.target.value})}
                placeholder="e.g., Near entrance, bedside table"
              />
            </div>
            
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                <Check size={18} />
                {isEdit ? 'Update Device' : 'Add Device'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };
  
  // Render delete confirmation modal
  const renderDeleteModal = () => {
    if (!deletingDevice) return null;
    
    return (
      <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
        <div className="modal-content delete-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Delete Device</h2>
            <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
              <X size={20} />
            </button>
          </div>
          
          <div className="delete-content">
            <AlertTriangle size={48} className="warning-icon" />
            <p>Are you sure you want to delete <strong>{deletingDevice.device_name}</strong>?</p>
            
            {deletingDevice.device_identifier && (
              <div className="smartthings-note">
                <Zap size={16} />
                This device is linked to SmartThings. It will be unlinked from the room but not deleted from SmartThings.
              </div>
            )}
          </div>
          
          <div className="delete-actions">
            <button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </button>
            <button className="btn-soft-delete" onClick={() => handleDeleteDevice(false)}>
              Soft Delete
            </button>
            <button className="btn-hard-delete" onClick={() => handleDeleteDevice(true)}>
              <Trash2 size={16} />
              Permanently Delete
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Render SmartThings virtual devices modal
  const renderDeviceDetailModal = () => {
    if (!showDetailModal) return null;

    // Use card data while full data loads
    const dev = detailDevice?.device || detailDevice || {};
    const history = detailDevice?.history || [];
    const liveStatus = detailDevice?.liveStatus || null;
    const config = getCategoryConfig(dev.category);
    const Icon = config.icon;

    const ACTION_COLORS = {
      ASSIGNED: '#3B82F6', MOVED: '#8B5CF6', REMOVED: '#EF4444',
      CREATED: '#10B981', UPDATED: '#F59E0B', SYNCED: '#06B6D4',
      DEACTIVATED: '#6B7280', ACTIVATED: '#10B981'
    };

    // Helper: pretty label for a SmartThings attribute value
    const formatAttrValue = (attr) => {
      const v = attr.value;
      if (typeof v === 'boolean') return v ? 'On' : 'Off';
      if (attr.capability === 'switch') return v === 'on' ? 'On' : 'Off';
      if (attr.unit) return `${v} ${attr.unit}`;
      if (typeof v === 'object') return JSON.stringify(v);
      return String(v);
    };

    const getAttrIcon = (cap) => {
      const map = {
        switch: ToggleLeft, switchLevel: TrendingUp, colorControl: Lightbulb,
        thermostatMode: Thermometer, temperature: Thermometer, relativeHumidity: Droplets,
        lock: Lock, motionSensor: Activity, contactSensor: Radio,
        battery: Battery, healthCheck: Activity
      };
      return map[cap] || Zap;
    };

    const tabs = [
      { id: 'info', label: 'Info', icon: Info },
      { id: 'live', label: 'Live Status', icon: Activity },
      { id: 'history', label: 'History', icon: History }
    ];

    return (
      <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
        <div className="modal-content device-detail-modal" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="device-detail-header" style={{ background: config.color + '15', borderBottom: `2px solid ${config.color}30` }}>
            <div className="device-detail-title">
              <div className="device-icon" style={{ backgroundColor: config.color + '25', color: config.color, width: 44, height: 44, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon size={22} />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>{dev.device_name || '—'}</h2>
                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{dev.device_type || config.label}</span>
                {dev.room_number && (
                  <span style={{ marginLeft: 8, fontSize: '0.75rem', background: '#e0f2fe', color: '#0369a1', padding: '2px 7px', borderRadius: 8 }}>
                    Room {dev.room_number}
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className={`device-status-badge ${dev.status}`} style={{ fontSize: '0.78rem' }}>
                {dev.status === 'online' && <Wifi size={13} />}
                {dev.status === 'offline' && <WifiOff size={13} />}
                {dev.status === 'error' && <AlertTriangle size={13} />}
                {dev.status || 'unknown'}
              </span>
              {loadingDetail && <RefreshCw size={16} className="spinning" style={{ color: '#94a3b8' }} />}
              <button className="modal-close" onClick={() => setShowDetailModal(false)}><X size={20} /></button>
            </div>
          </div>

          {/* Tabs */}
          <div className="device-detail-tabs">
            {tabs.map(t => (
              <button
                key={t.id}
                className={`detail-tab-btn ${detailTab === t.id ? 'active' : ''}`}
                onClick={() => setDetailTab(t.id)}
              >
                <t.icon size={14} /> {t.label}
                {t.id === 'history' && history.length > 0 && (
                  <span className="tab-badge">{history.length}</span>
                )}
                {t.id === 'live' && liveStatus?.readings?.length > 0 && (
                  <span className="tab-badge live">{liveStatus.readings.length}</span>
                )}
              </button>
            ))}
          </div>

          <div className="device-detail-body">
            {/* ── INFO TAB ── */}
            {detailTab === 'info' && (
              <div className="detail-grid">
                <div className="detail-section">
                  <h4><Cpu size={14} /> Device</h4>
                  <div className="detail-rows">
                    <div className="detail-row"><span>Manufacturer</span><span>{dev.manufacturer || '—'}</span></div>
                    <div className="detail-row"><span>Model</span><span>{dev.model || '—'}</span></div>
                    <div className="detail-row"><span>Category</span><span style={{ textTransform: 'capitalize' }}>{dev.category || '—'}</span></div>
                    <div className="detail-row"><span>Device Type</span><span>{dev.device_type || '—'}</span></div>
                    {dev.firmware_version && <div className="detail-row"><span>Firmware</span><span>{dev.firmware_version}</span></div>}
                    {dev.battery_level != null && (
                      <div className="detail-row">
                        <span>Battery</span>
                        <span className={dev.battery_level < 20 ? 'text-red' : ''}>{dev.battery_level}%</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="detail-section">
                  <h4><MapPin size={14} /> Location</h4>
                  <div className="detail-rows">
                    {dev.room_number && <div className="detail-row"><span>Room</span><span>Room {dev.room_number}</span></div>}
                    {dev.floor != null && <div className="detail-row"><span>Floor</span><span>Floor {dev.floor}</span></div>}
                    {dev.room_type && <div className="detail-row"><span>Room Type</span><span style={{ textTransform: 'capitalize' }}>{dev.room_type}</span></div>}
                    {dev.location_description && <div className="detail-row"><span>Description</span><span>{dev.location_description}</span></div>}
                  </div>
                </div>

                <div className="detail-section">
                  <h4><Calendar size={14} /> Timestamps</h4>
                  <div className="detail-rows">
                    {dev.created_at && <div className="detail-row"><span>Added</span><span>{new Date(dev.created_at).toLocaleDateString()}</span></div>}
                    {dev.last_seen && <div className="detail-row"><span>Last Seen</span><span>{new Date(dev.last_seen).toLocaleString()}</span></div>}
                    {dev.installation_date && <div className="detail-row"><span>Installed</span><span>{new Date(dev.installation_date).toLocaleDateString()}</span></div>}
                    {dev.warranty_expires && <div className="detail-row"><span>Warranty</span><span>{new Date(dev.warranty_expires).toLocaleDateString()}</span></div>}
                  </div>
                </div>

                {dev.device_identifier && (
                  <div className="detail-section full-width">
                    <h4><Zap size={14} /> SmartThings</h4>
                    <div className="detail-rows">
                      <div className="detail-row"><span>Device ID</span><span className="mono-text">{dev.device_identifier}</span></div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── LIVE STATUS TAB ── */}
            {detailTab === 'live' && (
              <div>
                {!dev.device_identifier ? (
                  <div className="detail-empty">
                    <WifiOff size={32} style={{ opacity: 0.3 }} />
                    <p>This device is not linked to SmartThings.<br />No live data available.</p>
                  </div>
                ) : loadingDetail ? (
                  <div className="detail-empty">
                    <RefreshCw size={28} className="spinning" />
                    <p>Fetching live readings from SmartThings...</p>
                  </div>
                ) : !liveStatus ? (
                  <div className="detail-empty">
                    <WifiOff size={32} style={{ opacity: 0.3 }} />
                    <p>SmartThings did not return live status for this device.</p>
                  </div>
                ) : liveStatus.readings.length === 0 ? (
                  <div className="detail-empty">
                    <Activity size={32} style={{ opacity: 0.3 }} />
                    <p>No attribute readings available.</p>
                  </div>
                ) : (
                  <div className="live-readings-grid">
                    {liveStatus.readings.map((r, i) => {
                      const AttrIcon = getAttrIcon(r.capability);
                      const isSwitch = r.capability === 'switch' && r.attribute === 'switch';
                      const isOn = r.value === 'on' || r.value === true;
                      return (
                        <div key={i} className={`live-reading-card ${isSwitch ? (isOn ? 'on' : 'off') : ''}`}>
                          <div className="live-reading-icon">
                            <AttrIcon size={18} />
                          </div>
                          <div className="live-reading-info">
                            <span className="live-reading-value">{formatAttrValue(r)}</span>
                            <span className="live-reading-label">{r.attribute}</span>
                          </div>
                          {r.timestamp && (
                            <span className="live-reading-time">{new Date(r.timestamp).toLocaleTimeString()}</span>
                          )}
                        </div>
                      );
                    })}
                    <div style={{ gridColumn: '1/-1', fontSize: '0.72rem', color: '#94a3b8', textAlign: 'right', paddingTop: 4 }}>
                      Fetched at {new Date(liveStatus.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── HISTORY TAB ── */}
            {detailTab === 'history' && (
              <div>
                {loadingDetail && history.length === 0 ? (
                  <div className="detail-empty">
                    <RefreshCw size={28} className="spinning" />
                    <p>Loading history...</p>
                  </div>
                ) : history.length === 0 ? (
                  <div className="detail-empty">
                    <History size={32} style={{ opacity: 0.3 }} />
                    <p>No history recorded for this device yet.</p>
                  </div>
                ) : (
                  <div className="history-timeline">
                    {history.map((h, i) => {
                      const actionColor = ACTION_COLORS[h.action] || '#6B7280';
                      return (
                        <div key={i} className="history-entry">
                          <div className="history-dot" style={{ background: actionColor }} />
                          <div className="history-content">
                            <div className="history-top">
                              <span className="history-action" style={{ color: actionColor }}>{h.action}</span>
                              {h.performed_by_name && (
                                <span className="history-user"><User size={11} /> {h.performed_by_name}</span>
                              )}
                              <span className="history-time">{new Date(h.created_at).toLocaleString()}</span>
                            </div>
                            {h.status === 'failed' && h.error_message && (
                              <p className="history-error">{h.error_message}</p>
                            )}
                            {h.new_state && Object.keys(h.new_state).length > 0 && (
                              <div className="history-state">
                                {Object.entries(h.new_state).map(([k, v]) => (
                                  <span key={k} className="state-chip">{k}: {String(v)}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="device-detail-footer">
            <button className="btn-secondary" onClick={() => { openEditModal(dev); setShowDetailModal(false); }}>
              <Edit2 size={14} /> Edit
            </button>
            {dev.device_identifier && (
              <button className="btn-secondary" onClick={() => { handleSyncDevice(dev); }}>
                <RefreshCw size={14} /> Sync Status
              </button>
            )}
            <button className="btn-cancel" style={{ marginLeft: 'auto' }} onClick={() => setShowDetailModal(false)}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderVirtualDevicesModal = () => {
    const CategoryIcon = selectedVirtualDevice
      ? (CATEGORY_CONFIG[selectedVirtualDevice.category]?.icon || Zap)
      : Zap;

    return (
      <div className="modal-overlay" onClick={() => { setShowVirtualModal(false); setSelectedVirtualDevice(null); setShowCreateVirtualForm(false); }}>
        <div className="modal-content discover-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '580px' }}>
          <div className="modal-header">
            <h2><Layers size={20} /> SmartThings Virtual Devices</h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                className="btn-primary"
                style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                onClick={handleOpenCreateVirtualForm}
                title="Create a brand-new virtual device in SmartThings"
              >
                <Plus size={14} /> Create New
              </button>
              <button className="modal-close" onClick={() => { setShowVirtualModal(false); setSelectedVirtualDevice(null); setShowCreateVirtualForm(false); }}>
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Create new virtual device form */}
          {showCreateVirtualForm && (
            <div className="virtual-create-panel">
              <div className="virtual-create-header">
                <Plus size={15} />
                <strong>Create New Virtual Device in SmartThings</strong>
                <button className="btn-link" style={{ marginLeft: 'auto' }} onClick={() => setShowCreateVirtualForm(false)}>
                  <X size={14} />
                </button>
              </div>
              <div className="virtual-create-form">
                <input
                  type="text"
                  className="virtual-room-select"
                  placeholder="Device name (e.g. Room 201 Smart Lock)"
                  value={createVirtualName}
                  onChange={e => setCreateVirtualName(e.target.value)}
                  style={{ flex: 2 }}
                />
                <select
                  className="virtual-room-select"
                  value={createVirtualPrototype}
                  onChange={e => setCreateVirtualPrototype(e.target.value)}
                  style={{ flex: 2 }}
                >
                  <option value="">— Device type —</option>
                  {virtualPrototypes.map(p => (
                    <option key={p.prototype} value={p.prototype}>{p.label}</option>
                  ))}
                </select>
                <select
                  className="virtual-room-select"
                  value={createVirtualRoomId}
                  onChange={e => setCreateVirtualRoomId(e.target.value)}
                  style={{ flex: 2 }}
                >
                  <option value="">— Assign to room (optional) —</option>
                  {rooms.map(room => (
                    <option key={room.room_id} value={room.room_id}>
                      Room {room.room_number}{room.floor ? ` · F${room.floor}` : ''}
                    </option>
                  ))}
                </select>
                <button
                  className="btn-primary"
                  disabled={!createVirtualName.trim() || !createVirtualPrototype || creatingVirtualDevice}
                  onClick={handleCreateVirtualDevice}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {creatingVirtualDevice ? <RefreshCw size={14} className="spinning" /> : <Check size={14} />}
                  Create
                </button>
              </div>
            </div>
          )}

          {/* Room assignment panel — shown when user clicks a device */}
          {selectedVirtualDevice && (
            <div className="virtual-assign-panel">
              <div className="virtual-assign-header">
                <CategoryIcon size={16} style={{ color: CATEGORY_CONFIG[selectedVirtualDevice.category]?.color || '#8B5CF6' }} />
                <strong>{selectedVirtualDevice.label}</strong>
                {selectedVirtualDevice.isLinked && (
                  <span className="virtual-badge linked">Currently: Room {selectedVirtualDevice.currentRoomNumber || '?'}</span>
                )}
              </div>
              <div className="virtual-assign-row">
                <select
                  className="virtual-room-select"
                  value={virtualAssignRoomId}
                  onChange={e => setVirtualAssignRoomId(e.target.value)}
                >
                  <option value="">— Select a room —</option>
                  {rooms.map(room => (
                    <option key={room.room_id} value={room.room_id}>
                      Room {room.room_number}{room.floor ? ` · Floor ${room.floor}` : ''}{room.room_type ? ` · ${room.room_type}` : ''}
                    </option>
                  ))}
                </select>
                <button
                  className="btn-primary"
                  disabled={!virtualAssignRoomId || assigningVirtualDevice}
                  onClick={handleAssignVirtualDevice}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {assigningVirtualDevice ? <RefreshCw size={14} className="spinning" /> : <Check size={14} />}
                  {selectedVirtualDevice.isLinked ? 'Move to Room' : 'Add to Room'}
                </button>
                <button className="btn-cancel" onClick={() => { setSelectedVirtualDevice(null); setVirtualAssignRoomId(''); }}>
                  <X size={14} />
                </button>
              </div>
            </div>
          )}

          <div className="discover-content">
            {loadingVirtual ? (
              <div className="discover-loading">
                <RefreshCw size={32} className="spinning" />
                <p>Loading virtual devices...</p>
              </div>
            ) : virtualDevices.length === 0 ? (
              <div className="discover-loading">
                <Layers size={32} style={{ opacity: 0.4 }} />
                <p style={{ color: '#9CA3AF', marginTop: '8px' }}>No virtual devices found for this hotel's SmartThings location.</p>
                <p style={{ fontSize: '0.8rem', color: '#6B7280', marginTop: '4px' }}>
                  Use the <strong>Create New</strong> button above to add your first virtual device.
                </p>
              </div>
            ) : (
              <>
                <div className="discover-summary">
                  <p>
                    <strong>{virtualDevices.length}</strong> virtual device{virtualDevices.length !== 1 ? 's' : ''}
                    {' · '}
                    <span style={{ color: '#10B981' }}>{virtualDevices.filter(d => d.isLinked).length} assigned</span>
                    {' · '}
                    <span style={{ color: '#F59E0B' }}>{virtualDevices.filter(d => !d.isLinked).length} unassigned</span>
                  </p>
                </div>
                <div className="discover-list">
                  {virtualDevices.map(device => {
                    const CatIcon = CATEGORY_CONFIG[device.category]?.icon || Zap;
                    const catColor = CATEGORY_CONFIG[device.category]?.color || '#8B5CF6';
                    const isSelected = selectedVirtualDevice?.deviceId === device.deviceId;
                    return (
                      <div
                        key={device.deviceId}
                        className={`discover-item virtual-device-item ${isSelected ? 'selected' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setSelectedVirtualDevice(isSelected ? null : device);
                          setVirtualAssignRoomId(device.currentRoomId ? String(device.currentRoomId) : '');
                        }}
                      >
                        <div className="discover-checkbox">
                          <CatIcon size={16} style={{ color: catColor }} />
                        </div>
                        <div className="discover-info">
                          <span className="discover-name">{device.label}</span>
                          <span className="discover-type">{device.deviceTypeName}</span>
                          {device.capabilities.length > 0 && (
                            <span className="discover-type" style={{ fontSize: '0.72rem', color: '#9CA3AF' }}>
                              {device.capabilities.slice(0, 3).join(', ')}{device.capabilities.length > 3 ? ` +${device.capabilities.length - 3}` : ''}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                          {device.isLinked ? (
                            <span className="virtual-badge linked">Room {device.currentRoomNumber}</span>
                          ) : (
                            <span className="virtual-badge unassigned">Unassigned</span>
                          )}
                          <span className={`discover-status ${device.healthState === 'ONLINE' ? 'online' : 'offline'}`} style={{ fontSize: '0.7rem' }}>
                            {device.healthState}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div className="discover-actions">
            <button className="btn-cancel" onClick={() => { setShowVirtualModal(false); setSelectedVirtualDevice(null); setShowCreateVirtualForm(false); }}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render SmartThings discovery modal
  const renderDiscoverModal = () => {
    const unlinkedDevices = smartThingsDevices.filter(d => !d.isLinked);
    
    const toggleDevice = (id) => {
      setSelectedDeviceIds(prev => 
        prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
      );
    };
    
    const selectAll = () => {
      setSelectedDeviceIds(unlinkedDevices.map(d => d.id));
    };
    
    return (
      <div className="modal-overlay" onClick={() => setShowDiscoverModal(false)}>
        <div className="modal-content discover-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>
              <Download size={20} />
              Discover SmartThings Devices
            </h2>
            <button className="modal-close" onClick={() => setShowDiscoverModal(false)}>
              <X size={20} />
            </button>
          </div>
          
          <div className="discover-content">
            {smartThingsDevices.length === 0 ? (
              <div className="discover-loading">
                <RefreshCw size={32} className="spinning" />
                <p>Discovering devices...</p>
              </div>
            ) : (
              <>
                <div className="discover-summary">
                  <p>Found <strong>{smartThingsDevices.length}</strong> devices</p>
                  <p><strong>{unlinkedDevices.length}</strong> available to import</p>
                </div>
                
                {unlinkedDevices.length > 0 && (
                  <button className="btn-select-all" onClick={selectAll}>
                    Select All Unlinked
                  </button>
                )}
                
                <div className="discover-list">
                  {smartThingsDevices.map(device => (
                    <div 
                      key={device.id}
                      className={`discover-item ${device.isLinked ? 'linked' : ''} ${selectedDeviceIds.includes(device.id) ? 'selected' : ''}`}
                      onClick={() => !device.isLinked && toggleDevice(device.id)}
                    >
                      <div className="discover-checkbox">
                        {device.isLinked ? (
                          <Check size={16} className="linked-icon" />
                        ) : (
                          <div className={`checkbox ${selectedDeviceIds.includes(device.id) ? 'checked' : ''}`}>
                            {selectedDeviceIds.includes(device.id) && <Check size={12} />}
                          </div>
                        )}
                      </div>
                      <div className="discover-info">
                        <span className="discover-name">{device.name}</span>
                        <span className="discover-type">{device.type}</span>
                      </div>
                      <span className={`discover-status ${device.status?.toLowerCase()}`}>
                        {device.status}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          
          <div className="discover-actions">
            <button className="btn-cancel" onClick={() => setShowDiscoverModal(false)}>
              Cancel
            </button>
            <button 
              className="btn-primary"
              disabled={selectedDeviceIds.length === 0}
              onClick={() => handleImportDevices(selectedDeviceIds)}
            >
              <Upload size={16} />
              Import {selectedDeviceIds.length} Devices
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="iot-dashboard-loading">
        <RefreshCw size={48} className="spinning" />
        <p>Loading IoT Dashboard...</p>
      </div>
    );
  }
  
  return (
    <div className="manager-iot-dashboard">
      {/* Header */}
      <div className="iot-dashboard-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/manager')}>
            <ArrowLeft size={20} />
          </button>
          <div className="header-info">
            <h1>🏠 IoT Device Management</h1>
            <p className="subtitle">
              {hotelInfo?.name || 'Managing smart devices for your hotel'}
            </p>
          </div>
        </div>
        <div className="header-actions">
          <button 
            className={`btn-icon ${refreshing ? 'spinning' : ''}`} 
            onClick={handleRefresh}
            disabled={refreshing}
            title="Refresh"
          >
            <RefreshCw size={20} />
          </button>
          <button 
            className="btn-secondary"
            onClick={handleViewVirtualDevices}
            title="View SmartThings Virtual Devices"
          >
            <Layers size={18} />
            Virtual Devices
          </button>
          <button 
            className="btn-secondary"
            onClick={handleDiscoverDevices}
            title="Discover SmartThings Devices"
          >
            <Download size={18} />
            Discover
          </button>
          <button
            className="btn-secondary"
            onClick={handleSyncAllDevices}
            disabled={syncingAll}
            title="Sync all SmartThings devices"
          >
            <RefreshCw size={18} className={syncingAll ? 'spinning' : ''} />
            {syncingAll ? 'Syncing...' : 'Sync Devices'}
          </button>
          <button
            className="btn-secondary"
            onClick={handleCreateBackup}
            disabled={creatingBackup}
            title="Create IoT backup"
          >
            <Upload size={18} />
            {creatingBackup ? 'Creating...' : 'Create Backup'}
          </button>
          <button 
            className="btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={18} />
            Add Device
          </button>
        </div>
      </div>
      
      {/* Statistics */}
      {renderStatistics()}
      
      {/* Alerts */}
      {renderAlerts()}

      {/* Backup & History */}
      {renderBackupHistory()}
      
      {/* Filters */}
      <div className="iot-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search devices..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        
        <div className="filter-group">
          <Filter size={16} />
          
          <select 
            value={selectedFloor} 
            onChange={e => setSelectedFloor(e.target.value)}
          >
            <option value="all">All Floors</option>
            {floors.map(floor => (
              <option key={floor.floor} value={floor.floor}>
                {floor.floor_label} ({floor.device_count} devices)
              </option>
            ))}
          </select>
          
          <select 
            value={selectedRoom} 
            onChange={e => setSelectedRoom(e.target.value)}
          >
            <option value="all">All Rooms</option>
            {rooms.map(room => (
              <option key={room.room_id} value={room.room_id}>
                Room {room.room_number} {room.floor ? `(F${room.floor})` : ''}
              </option>
            ))}
          </select>
          
          <select 
            value={selectedCategory} 
            onChange={e => setSelectedCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            {DEVICE_CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
          
          <select 
            value={selectedStatus} 
            onChange={e => setSelectedStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            {DEVICE_STATUSES.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>
        
        <div className="view-toggle">
          <button 
            className={viewMode === 'floor' ? 'active' : ''}
            onClick={() => setViewMode('floor')}
            title="Floor View"
          >
            <Building size={18} />
          </button>
          <button 
            className={viewMode === 'grid' ? 'active' : ''}
            onClick={() => setViewMode('grid')}
            title="Grid View"
          >
            <LayoutGrid size={18} />
          </button>
          <button 
            className={viewMode === 'list' ? 'active' : ''}
            onClick={() => setViewMode('list')}
            title="List View"
          >
            <List size={18} />
          </button>
        </div>
      </div>
      
      {/* Devices Grid/List */}
      {rooms.length === 0 && filteredDevices.length === 0 ? (
        <div className="no-devices">
          <Zap size={48} />
          <h3>No devices found</h3>
          <p>Add your first IoT device or discover devices from SmartThings</p>
          <div className="no-devices-actions">
            <button className="btn-secondary" onClick={handleDiscoverDevices}>
              <Download size={18} />
              Discover Devices
            </button>
            <button className="btn-primary" onClick={() => setShowAddModal(true)}>
              <Plus size={18} />
              Add Manually
            </button>
          </div>
        </div>
      ) : viewMode === 'floor' ? (
        <div className="iot-floor-view">
          {(() => {
            // Build floor structure from the flat rooms list (rooms-first approach)
            const floorMap = {};
            const filteredRooms = selectedFloor !== 'all'
              ? rooms.filter(r => String(r.floor ?? 0) === String(selectedFloor))
              : rooms;

            filteredRooms.forEach(room => {
              const f = room.floor ?? 0;
              if (!floorMap[f]) floorMap[f] = { floor: f, floor_label: `Floor ${f}`, rooms: [] };
              floorMap[f].rooms.push(room);
            });

            // Also include any floors that only appear in devices (edge case)
            filteredDevices.forEach(d => {
              const f = d.floor ?? 0;
              if (!floorMap[f]) floorMap[f] = { floor: f, floor_label: `Floor ${f}`, rooms: [] };
            });

            const floorList = Object.values(floorMap).sort((a, b) => a.floor - b.floor);

            return floorList.map(floorGroup => {
              // All devices on this floor
              const floorDevices = filteredDevices.filter(d =>
                (d.floor ?? 0) === floorGroup.floor ||
                floorGroup.rooms.some(r => r.room_id === d.room_id)
              );

              return (
                <div key={floorGroup.floor} className="floor-section">
                  <div
                    className="floor-header"
                    onClick={() => toggleFloorExpansion(floorGroup.floor)}
                  >
                    <div className="floor-title">
                      <Layers size={20} />
                      <h3>{floorGroup.floor_label}</h3>
                      <span className="floor-device-count">
                        {floorGroup.rooms.length} room{floorGroup.rooms.length !== 1 ? 's' : ''}
                        {floorDevices.length > 0 && ` · ${floorDevices.length} device${floorDevices.length !== 1 ? 's' : ''}`}
                      </span>
                    </div>
                    <div className="floor-stats">
                      <span className="online-count">
                        <Wifi size={14} />
                        {floorDevices.filter(d => d.status === 'online').length} online
                      </span>
                      {floorDevices.filter(d => d.status === 'error').length > 0 && (
                        <span className="error-count">
                          <AlertTriangle size={14} />
                          {floorDevices.filter(d => d.status === 'error').length} errors
                        </span>
                      )}
                      {expandedFloors[floorGroup.floor] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </div>
                  {expandedFloors[floorGroup.floor] && (
                    <div className="floor-devices">
                      {floorGroup.rooms.map(room => {
                        const roomDevices = filteredDevices.filter(d => d.room_id === room.room_id);
                        return (
                          <div key={room.room_id} className="room-section">
                            <h4 className="room-subheader">
                              Room {room.room_number}
                              {room.room_type && room.room_type !== 'Room' && (
                                <span className="room-type-label"> · {room.room_type}</span>
                              )}
                              <span className="room-device-badge">
                                {roomDevices.length} device{roomDevices.length !== 1 ? 's' : ''}
                              </span>
                            </h4>
                            {roomDevices.length > 0 ? (
                              <div className="room-devices-grid">
                                {roomDevices.map(renderDeviceCard)}
                              </div>
                            ) : (
                              <div className="room-empty-devices">
                                <Zap size={16} style={{ opacity: 0.3 }} />
                                <span>No devices assigned</span>
                                <button
                                  className="btn-link"
                                  onClick={() => setShowAddModal(true)}
                                >
                                  + Add device
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* Unassigned devices on this floor */}
                      {filteredDevices.filter(d =>
                        (d.floor ?? 0) === floorGroup.floor && !d.room_id
                      ).length > 0 && (
                        <div className="room-section">
                          <h4 className="room-subheader">Unassigned</h4>
                          <div className="room-devices-grid">
                            {filteredDevices
                              .filter(d => (d.floor ?? 0) === floorGroup.floor && !d.room_id)
                              .map(renderDeviceCard)
                            }
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="iot-devices-grid">
          {filteredDevices.map(renderDeviceCard)}
        </div>
      ) : (
        <div className="iot-devices-list">
          {Object.entries(devicesByRoom).map(([roomKey, roomDevices]) => (
            <div key={roomKey} className="room-group">
              <h3 className="room-header">{roomKey}</h3>
              <div className="room-devices">
                {roomDevices.map(renderDeviceCard)}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Modals */}
      {showAddModal && renderDeviceFormModal(false)}
      {showEditModal && renderDeviceFormModal(true)}
      {showDeleteModal && renderDeleteModal()}
      {showDiscoverModal && renderDiscoverModal()}
      {showVirtualModal && renderVirtualDevicesModal()}
      {showDetailModal && renderDeviceDetailModal()}
    </div>
  );
};

export default ManagerIoTDashboard;
